import { AppThunk } from 'store';
import { setConnecting, setLoading } from 'features/audio/audioSlice';

let abortController = new AbortController();
let fetchAudioTimeout: NodeJS.Timeout;

const fetchAudioUrl = async (
  src: string,
  audioType: string,
  setSrc: (_: string) => void,
  retries = 0
) => {
  if (audioType === 'recording') {
    setSrc(src);
  } else {
    if (abortController) abortController.abort();
    abortController = new AbortController();
    const signal = abortController.signal;

    const scheduleRetry = () => {
      if (retries >= 50) {
        fetchAudioAbort();
        setSrc(src);
        return;
      }
      const retryDelay = retries < 5 ? 2000 : 6000;
      fetchAudioTimeout = setTimeout(() => {
        console.log('[Audio] - Fetching audio: ', src);
        fetchAudioUrl(src, audioType, setSrc, retries + 1);
      }, retryDelay);
    };

    try {
      const response = await fetch(src, { method: 'GET', signal });

      if (!response.ok || !response.body) {
        scheduleRetry();
        return;
      }

      const reader = response.body.getReader();
      let hasData = false;
      const timeoutId = setTimeout(() => {
        reader.cancel().catch(() => undefined);
      }, 5000);

      try {
        const { value, done } = await reader.read();
        clearTimeout(timeoutId);
        hasData = !done && !!(value && value.length > 0);
      } catch {
        hasData = false;
      } finally {
        try {
          await reader.cancel();
        } catch {
          // ignore — already cancelled or stream closed
        }
      }

      if (hasData) {
        fetchAudioAbort();
        setSrc(src);
      } else {
        scheduleRetry();
      }
    } catch (err) {
      if (err.name == 'AbortError') {
        console.log('[Audio] - Abort Error');
      } else {
        scheduleRetry();
      }
    }
  }
};

const delay = (delayInms: number) => {
  return new Promise((resolve) => setTimeout(resolve, delayInms));
};

export const fetchAudioAbort = () => {
  if (abortController.signal && abortController.signal.aborted) {
    return;
  }
  console.log('[Audio] - fetchAudioAbort');
  clearInterval(fetchAudioTimeout);
  abortController.abort();
};

export const checkAndPlayAudio =
  (
    src: string,
    audioType: string,
    setSrc: (_: string) => void,
    withDelay?: boolean
  ): AppThunk =>
  async (dispatch) => {
    dispatch(setLoading(true));
    dispatch(setConnecting(true));
    await delay(withDelay ? 2000 : 0);
    await fetchAudioUrl(src, audioType, setSrc);
  };
